import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureMdExtension, extname, titleFromPath, type VaultFileEntry } from '@rbv/shared';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';
import { index } from '../worker/client.js';
import { vaultFileUrl } from '../navigation.js';
import { getRecents, recordRecent } from './hooks.js';

interface Row {
  key: string;
  title: string;
  detail: string;
  create?: boolean;
  run: () => void;
}

export function QuickSwitcher() {
  const open = useApp((s) => s.quickSwitcherOpen);
  const setOpen = useApp((s) => s.setQuickSwitcherOpen);

  // Record the MRU on every note open, even while the switcher is closed.
  useEffect(
    () =>
      useApp.subscribe((s, prev) => {
        if (
          s.view.kind === 'note' &&
          !(prev.view.kind === 'note' && prev.view.id === s.view.id)
        ) {
          recordRecent(s.view.id);
        }
      }),
    [],
  );

  if (!open) return null;
  // Separate component so each opening starts with fresh query/selection.
  return <SwitcherModal onClose={() => setOpen(false)} />;
}

function SwitcherModal({ onClose }: { onClose: () => void }) {
  const openNote = useApp((s) => s.openNote);
  const openView = useApp((s) => s.openView);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const openTarget = useCallback(
    (id: string, heading?: string) => {
      const entry = vault.getEntry(id);
      if (!entry || entry.deleted) return;
      if (entry.kind === 'canvas') openView({ kind: 'canvas', id });
      else if (extname(entry.path) === '.base') openView({ kind: 'base', id });
      else if (entry.kind === 'file') {
        window.open(vaultFileUrl(entry.path), '_blank', 'noopener');
        onClose();
      } else openNote(id, heading ? { heading } : undefined);
    },
    [openNote, openView, onClose],
  );

  useEffect(() => {
    const q = query.trim();
    let stale = false;

    const recents = getRecents()
      .map((id) => ({ id, entry: vault.getEntry(id) }))
      .filter((r): r is { id: string; entry: VaultFileEntry } => !!r.entry && !r.entry.deleted);

    const recentRows = (filter: string): Row[] =>
      recents
        .filter(
          (r) =>
            !filter ||
            titleFromPath(r.entry.path).toLowerCase().includes(filter.toLowerCase()),
        )
        .slice(0, filter ? 3 : 12)
        .map((r) => ({
          key: `recent:${r.id}`,
          title: titleFromPath(r.entry.path),
          detail: r.entry.path,
          run: () => openTarget(r.id),
        }));

    if (!q) {
      setRows(recentRows(''));
      setSel(0);
      return;
    }

    void index.suggestLinks(q, undefined, 10).then((sugs) => {
      if (stale) return;
      const out: Row[] = [];
      const seen = new Set<string>();
      for (const r of recentRows(q)) {
        seen.add(r.key.slice('recent:'.length));
        out.push(r);
      }
      for (const s of sugs) {
        if (!s.id) continue; // current-doc heading rows are editor-only
        if (s.kind !== 'heading') {
          if (seen.has(s.id)) continue;
          seen.add(s.id);
        }
        const heading =
          s.kind === 'heading' ? s.linkText.split('#').slice(1).join('#') : undefined;
        out.push({
          key: `${s.kind}:${s.id}:${s.linkText}`,
          title: s.display,
          detail: s.path,
          run: () => openTarget(s.id, heading),
        });
      }
      const exact = out.some((r) => r.title.toLowerCase() === q.toLowerCase());
      if (!exact && !q.includes('#')) {
        out.push({
          key: 'create',
          title: `Create note “${q}”`,
          detail: ensureMdExtension(q),
          create: true,
          run: () => {
            void vault.createNote(ensureMdExtension(q)).then((id) => openNote(id));
          },
        });
      }
      setRows(out.slice(0, 15));
      setSel(0);
    });
    return () => {
      stale = true;
    };
  }, [query, openTarget, openNote]);

  useEffect(() => {
    const el = listRef.current?.children[sel] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      rows[sel]?.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="qs-overlay" onClick={onClose}>
      <div className="qs-modal" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="qs-input"
          placeholder="Find or create a note…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
        />
        <div className="qs-list" ref={listRef}>
          {rows.map((r, i) => (
            <button
              key={r.key}
              className={`qs-row${i === sel ? ' selected' : ''}${r.create ? ' create' : ''}`}
              onMouseEnter={() => setSel(i)}
              onClick={() => r.run()}
            >
              <span className="qs-title">{r.title}</span>
              <span className="qs-detail">{r.detail}</span>
            </button>
          ))}
          {rows.length === 0 && (
            <div className="pane-empty">
              {query ? 'No matches.' : 'No recent notes yet — start typing to find a note.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
